/**
 * Canonical event-date helpers — Task #112.
 *
 * Event dates on a booking are a **calendar day**, not an instant. The
 * authoritative wire/storage format is `YYYY-MM-DD` (Postgres `date`,
 * surfaced as a string end-to-end). All code that needs to compare,
 * sort, or display an event date should go through these helpers
 * instead of calling `new Date(...)` directly, which would re-introduce
 * the timezone-drift bugs this task fixes (a UTC-midnight ISO string
 * rendered in `America/Chicago` shifts back one day).
 */

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Accepts the loose set of values event-date columns historically held
 * (legacy `Date`, ISO timestamp string, plain `YYYY-MM-DD`) and returns
 * the canonical `YYYY-MM-DD` string for the calendar day the user
 * actually meant.
 *
 * Rule (mirrors the data migration): if the input is a `YYYY-MM-DD`
 * string, return it as-is. If the input is a timestamp, take its date
 * part — both write paths (UTC-midnight via `.toISOString()` and
 * local-noon via the inline cell) round-trip to the same calendar day
 * that way.
 */
export function toEventDate(
  value: string | Date | null | undefined,
): string | null {
  if (value == null) return null;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    const y = value.getUTCFullYear();
    const m = String(value.getUTCMonth() + 1).padStart(2, "0");
    const d = String(value.getUTCDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (ISO_DATE_RE.test(trimmed)) return trimmed;
  // ISO timestamp — strip time component. We intentionally use the
  // literal string prefix rather than `new Date(...).toISOString()` so
  // we don't shift days when the server tz isn't UTC.
  const tIdx = trimmed.indexOf("T");
  const head = tIdx >= 0 ? trimmed.slice(0, tIdx) : trimmed.slice(0, 10);
  return ISO_DATE_RE.test(head) ? head : null;
}

/**
 * Stable comparator usable with `Array#sort`. Nulls sort last in
 * ascending order. Comparing the `YYYY-MM-DD` strings lexicographically
 * is equivalent to comparing calendar days.
 */
export function compareEventDate(
  a: string | Date | null | undefined,
  b: string | Date | null | undefined,
): number {
  const da = toEventDate(a);
  const db = toEventDate(b);
  if (da === db) return 0;
  if (da == null) return 1;
  if (db == null) return -1;
  return da < db ? -1 : 1;
}

/**
 * Renders an event date for human consumption. Returns "" for null so
 * callers can drop straight into JSX. The format is intentionally
 * locale-independent and timezone-free.
 */
export function formatEventDate(
  value: string | Date | null | undefined,
  opts: { variant?: "long" | "short" | "iso" } = {},
): string {
  const iso = toEventDate(value);
  if (!iso) return "";
  if (opts.variant === "iso") return iso;
  const [y, m, d] = iso.split("-").map((n) => Number.parseInt(n, 10));
  if (!y || !m || !d) return iso;
  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];
  const short = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];
  if (opts.variant === "short") return `${short[m - 1]} ${d}`;
  return `${months[m - 1]} ${d}, ${y}`;
}

/**
 * Parses an event-date string into a Date positioned at **noon UTC**
 * of that calendar day. Noon UTC is far enough from a tz boundary that
 * `.toLocaleDateString()` / `date-fns format()` will report the same
 * calendar day in every IANA zone. Use only when an existing API
 * demands a `Date`; prefer `formatEventDate` for display.
 */
export function parseEventDate(
  value: string | Date | null | undefined,
): Date | null {
  const iso = toEventDate(value);
  if (!iso) return null;
  const d = new Date(`${iso}T12:00:00.000Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Validates a value is exactly `YYYY-MM-DD`. */
export function isEventDateString(value: unknown): value is string {
  return typeof value === "string" && ISO_DATE_RE.test(value);
}
